import { Link } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MINIMUM_AGE, TERMS_VERSION } from '@lineage/core';
import { useState, type ReactNode } from 'react';
import { useT } from '../i18n';
import { api, type Me } from '../lib/api';
import { setConsent, useShowBanner } from '../lib/analytics';
import { useAuth } from '../lib/auth';
import { keys, useMe } from '../lib/queries';

/** "[Terms of Service] and [Privacy Policy]" as links that open in a new tab, so a half-filled form isn't lost. */
function TermsLinks() {
  const t = useT().legal;
  return (
    <>
      <Link to="/terms" target="_blank">{t.terms}</Link> {t.and} <Link to="/privacy" target="_blank">{t.privacy}</Link>
    </>
  );
}

/** Required tick box: "I agree to the Terms of Service and Privacy Policy, and I am at least 13 years old." */
export function AgreeCheckbox({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  const t = useT().legal;
  return (
    <label className="row" style={{ alignItems: 'flex-start', flexWrap: 'nowrap', gap: 10, fontSize: 14 }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} required style={{ marginTop: 3, width: 18, height: 18, flex: 'none' }} />
      <span>
        {t.agreeBefore} <TermsLinks />
        {t.agreeAfter(MINIMUM_AGE)}
      </span>
    </label>
  );
}

/** Notice under the sign-in buttons. */
export function ContinuingNotice() {
  const t = useT().legal;
  return (
    <p className="muted" style={{ fontSize: 13, textAlign: 'center', margin: 0 }}>
      {t.continuingBefore} <TermsLinks />
      .
    </p>
  );
}

/** Records agreement to the current terms on the account. */
export function useAcceptTerms() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ user: Me['user'] }>('PATCH', '/me', { acceptTerms: TERMS_VERSION }),
    onSuccess: (r) => qc.setQueryData<Me>(keys.me, (old) => (old ? { ...old, user: { ...old.user, ...r.user } } : old)),
  });
}

/**
 * True when the signed-in account hasn't agreed to the current terms. Older API versions don't send the field
 * (undefined), and an account that can't load (offline) isn't blocked.
 */
export function useNeedsTerms(): { needs: boolean; updated: boolean } {
  const { user } = useAuth();
  const me = useMe();
  const v = me.data?.user.termsVersion;
  if (!user || v === undefined) return { needs: false, updated: false };
  return { needs: v !== TERMS_VERSION, updated: v !== null };
}

/** Agreement screen for accounts that haven't accepted the current terms (new Google sign-ins, existing members). */
export function TermsPanel() {
  const t = useT().legal;
  const { signOut } = useAuth();
  const qc = useQueryClient();
  const { updated } = useNeedsTerms();
  const [agree, setAgree] = useState(false);
  const accept = useAcceptTerms();
  return (
    <div className="card-panel stack" style={{ textAlign: 'left' }}>
      <h1 style={{ fontSize: 28 }}>{updated ? t.gateTitleUpdated : t.gateTitleNew}</h1>
      <p className="muted">{t.gateBody}</p>
      <AgreeCheckbox checked={agree} onChange={setAgree} />
      {accept.isError && <div className="error" role="alert">{t.couldntSave}</div>}
      <button className="btn btn-primary btn-block" disabled={!agree || accept.isPending} onClick={() => accept.mutate()}>
        {t.agreeAndContinue}
      </button>
      <button className="btn btn-ghost btn-sm" type="button" onClick={async () => { await signOut(); qc.clear(); }}>
        {useT().settings.signOut}
      </button>
    </div>
  );
}

/** Shows the agreement screen in place of signed-in pages until the current terms are accepted. */
export function TermsGate({ children }: { children: ReactNode }) {
  const { needs } = useNeedsTerms();
  if (!needs) return <>{children}</>;
  return (
    <div className="center">
      <div className="stack" style={{ maxWidth: 420, width: '100%' }}>
        <span className="wordmark">Lineage</span>
        <TermsPanel />
      </div>
    </div>
  );
}

/** Analytics banner, shown only where the law needs opt-in (lib/analytics.ts). Allow and Decline weigh the same. */
export function ConsentBanner() {
  const t = useT().legal;
  const show = useShowBanner();
  if (!show) return null;
  return (
    <div role="region" aria-label={t.learnMore} className="consent-banner">
      <p style={{ margin: 0 }}>
        {t.bannerText} <Link to="/privacy">{t.learnMore}</Link>
      </p>
      <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
        <button className="btn btn-sm" onClick={() => setConsent('denied')}>{t.decline}</button>
        <button className="btn btn-sm" onClick={() => setConsent('granted')}>{t.allow}</button>
      </div>
    </div>
  );
}
